/**
 * Sort order for System Card's sortable tables (the price map).
 *
 * Pure so the ordering rules are unit-tested apart from the component; holds
 * no class strings, so living under `src/lib` is safe for Tailwind.
 */
import type { DataTable, TableCell } from "@/constants/systemCard.types";

import { htmlToPlainText } from "./systemCardShare";

export type SortDirection = "asc" | "desc";

/** `null` is the edition's authored order. */
export type SortState = { column: number; direction: SortDirection } | null;

/** One header click: asc, then desc, then back to the authored order; a new column starts asc. */
export function nextSortState(current: SortState, column: number): SortState {
  if (!current || current.column !== column) return { column, direction: "asc" };
  return current.direction === "asc" ? { column, direction: "desc" } : null;
}

type Key = { rank: 0; value: number } | { rank: 1; value: string } | { rank: 2 };

/** rank orders kinds independent of direction: numbers, then text, then empty cells. */
function sortKey(cell: TableCell | undefined): Key {
  const raw = cell?.sortValue ?? htmlToPlainText(cell?.html ?? "");
  if (typeof raw === "number" && Number.isFinite(raw)) return { rank: 0, value: raw };
  const text = String(raw).trim();
  return text ? { rank: 1, value: text } : { rank: 2 };
}

/**
 * Rows sorted by one column, as a new array. Direction flips only comparisons
 * within a kind, so blanks and "n/a" text stay below the numbers either way.
 * Stable: ties keep the authored order.
 */
export function sortTableRows(rows: DataTable["rows"], column: number, direction: SortDirection): DataTable["rows"] {
  const sign = direction === "asc" ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, key: sortKey(row[column]) }))
    .sort((a, b) => {
      if (a.key.rank !== b.key.rank) return a.key.rank - b.key.rank;
      let cmp = 0;
      if (a.key.rank === 0 && b.key.rank === 0) cmp = a.key.value - b.key.value;
      else if (a.key.rank === 1 && b.key.rank === 1)
        cmp = a.key.value.localeCompare(b.key.value, "en", { numeric: true, sensitivity: "base" });
      return cmp * sign || a.index - b.index;
    })
    .map(({ row }) => row);
}
